// Iter 111 · Phase P2.A — Professional Tax Master editor.
//
// Focused CRUD-with-supersession UI for the Professional-Tax rules
// stored on `payroll_compliance_settings`. Uses the thin PT overlay
// API (see routes_payroll_compliance.py §Iter 111 · P2.A) so PF /
// ESI / LWF / TDS / Gratuity / Bonus and other states' PT rules are
// guaranteed untouched.
//
// UX:
//   • Left column: list of states with their current PT summary.
//   • Right column (in dialog): per-state slab table, cadence toggle,
//     half-yearly months, gender exemption, month-of-year adjustments.
//   • "Save as new effective-dated version" — creates a new compliance
//     document that supersedes the current-active one. Requires
//     effective_from > today.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Buildings, Plus, FloppyDisk, X, PencilSimple, WarningCircle, Trash, Info } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const MONTH_LABELS = { "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun", "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };

const EMPTY_STATE_EDIT = {
    state: "",
    effective_from: "",
    slabs: [],
    cadence: "monthly",
    half_yearly_months: [],
    gender_exemption_enabled: false,
    gender_exemption: { gender: "female", max_gross: 0 },
    period_adjustments: {},
    change_note: "",
};

export default function PtMaster() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    const [meta, setMeta] = useState({ cadences: [], months: [], genders: [], common_states: [] });
    const [list, setList] = useState({ as_of: null, source_doc_id: null, source_effective_from: null, items: [] });
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [draft, setDraft] = useState(EMPTY_STATE_EDIT);
    const [historyState, setHistoryState] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [m, l] = await Promise.all([
                api.get("/payroll/compliance/pt-master/meta"),
                api.get("/payroll/compliance/pt-master/list"),
            ]);
            setMeta(m.data);
            setList(l.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load PT Master"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const openEdit = (row) => {
        // Pre-fill the draft from the current row (edit mode) or empty (new state).
        const isNew = !row;
        setDraft({
            state: row?.state || "",
            effective_from: "",  // must be strictly future — force operator to type it
            slabs: (row?.slabs || []).map((s) => ({ ...s })),
            cadence: row?.cadence || "monthly",
            half_yearly_months: row?.half_yearly_months || [],
            gender_exemption_enabled: !!row?.gender_exemption,
            gender_exemption: row?.gender_exemption || { gender: "female", max_gross: 0 },
            period_adjustments: { ...(row?.period_adjustments || {}) },
            change_note: "",
            _mode: isNew ? "new" : "edit",
        });
        setEditOpen(true);
    };

    const loadHistory = async (state) => {
        try {
            const { data } = await api.get(`/payroll/compliance/pt-master/${encodeURIComponent(state)}`);
            setHistoryState(state);
            setHistoryRows(data.history || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load history"));
        }
    };

    const addSlab = () => setDraft((d) => ({
        ...d,
        slabs: [...d.slabs, { min_gross: 0, max_gross: null, monthly_tax: 0 }],
    }));
    const updateSlab = (idx, patch) => setDraft((d) => ({
        ...d,
        slabs: d.slabs.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
    const removeSlab = (idx) => setDraft((d) => ({
        ...d,
        slabs: d.slabs.filter((_, i) => i !== idx),
    }));

    const toggleHalfYearlyMonth = (m) => setDraft((d) => ({
        ...d,
        half_yearly_months: d.half_yearly_months.includes(m)
            ? d.half_yearly_months.filter((x) => x !== m)
            : [...d.half_yearly_months, m].sort(),
    }));
    const setPeriodAdj = (month, val) => setDraft((d) => {
        const next = { ...d.period_adjustments };
        const n = Number(val);
        if (!val || Number.isNaN(n) || n === 0) {
            delete next[month];
        } else {
            next[month] = n;
        }
        return { ...d, period_adjustments: next };
    });

    const submit = async () => {
        if (!draft.state.trim()) {
            toast.error("State name is required");
            return;
        }
        if (!draft.effective_from) {
            toast.error("Effective-from date is required (must be strictly future)");
            return;
        }
        if (draft.slabs.length === 0 && draft._mode === "new") {
            toast.error("Add at least one slab row");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                state: draft.state.trim(),
                effective_from: draft.effective_from,
                slabs: draft.slabs.map((s) => ({
                    min_gross: Number(s.min_gross || 0),
                    max_gross: s.max_gross === "" || s.max_gross == null ? null : Number(s.max_gross),
                    monthly_tax: Number(s.monthly_tax || 0),
                })),
                cadence: draft.cadence,
                half_yearly_months: draft.cadence === "half_yearly" ? draft.half_yearly_months : [],
                gender_exemption: draft.gender_exemption_enabled ? {
                    gender: draft.gender_exemption.gender,
                    max_gross: Number(draft.gender_exemption.max_gross || 0),
                } : null,
                period_adjustments: Object.fromEntries(
                    Object.entries(draft.period_adjustments).map(([k, v]) => [k, Number(v)]),
                ),
                change_note: draft.change_note.trim(),
            };
            const { data } = await api.post("/payroll/compliance/pt-master/upsert", payload);
            toast.success(`Saved. New compliance version ${data.new_doc_id} takes effect ${data.effective_from}.`);
            setEditOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const stateOptions = useMemo(() => {
        const existing = new Set((list.items || []).map((i) => i.state));
        return (meta.common_states || []).filter((s) => !existing.has(s));
    }, [meta, list]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="pt-master-page">
            <PageHeader
                eyebrow="Payroll Configuration"
                title="Professional Tax Master"
                description={`Per-state PT rules. Editing creates a new effective-dated compliance version — PF, ESI, LWF, TDS, Gratuity and Bonus are guaranteed untouched.${list.source_doc_id ? ` Current source: ${list.source_doc_id} (effective ${list.source_effective_from}).` : ""}`}
                actions={canEdit ? (
                    <Button size="sm" onClick={() => openEdit(null)} data-testid="pt-add-state-btn">
                        <Plus size={14} className="mr-1" /> Add State
                    </Button>
                ) : null}
            />

            {!list.source_doc_id && !loading && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="pt-no-compliance-banner">
                    <WarningCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="text-sm font-semibold text-amber-900">No base compliance document</div>
                        <div className="text-xs text-amber-800 mt-0.5">
                            The PT Master needs a base <code>payroll_compliance_settings</code> document to clone from. Ask a Compliance admin to post one via <code>POST /api/payroll/compliance</code> first.
                        </div>
                    </div>
                </div>
            )}

            <section className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pt-state-grid">
                {loading ? (
                    <div className="col-span-full text-sm text-slate-400 py-10 text-center">Loading…</div>
                ) : (list.items || []).length === 0 ? (
                    <div className="col-span-full text-sm text-slate-400 py-10 text-center" data-testid="pt-empty">
                        No states configured yet. {canEdit && "Click Add State to seed the first one."}
                    </div>
                ) : (list.items || []).map((row) => (
                    <div
                        key={row.state}
                        className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-2"
                        data-testid={`pt-state-card-${row.state.replace(/\s+/g, "-")}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                                <Buildings size={18} className="text-indigo-500" />
                                <span className="text-sm font-semibold text-slate-900">{row.state}</span>
                            </div>
                            <Badge variant="outline" className="text-[10px] uppercase">{row.cadence}</Badge>
                        </div>
                        <div className="text-xs text-slate-600">
                            {row.slabs.length} slab{row.slabs.length === 1 ? "" : "s"}
                            {row.gender_exemption && (
                                <span className="ml-2 text-emerald-700">
                                    · {row.gender_exemption.gender} exempt ≤ {inr(row.gender_exemption.max_gross)}
                                </span>
                            )}
                        </div>
                        {row.slabs.length > 0 && (
                            <div className="text-[11px] text-slate-500 space-y-0.5" data-testid={`pt-slabs-${row.state.replace(/\s+/g, "-")}`}>
                                {row.slabs.slice(0, 3).map((s, i) => (
                                    <div key={i}>
                                        {inr(s.min_gross)} → {s.max_gross == null ? "∞" : inr(s.max_gross)}: {inr(s.monthly_tax)}/mo
                                    </div>
                                ))}
                                {row.slabs.length > 3 && <div className="text-slate-400">+ {row.slabs.length - 3} more</div>}
                            </div>
                        )}
                        {Object.keys(row.period_adjustments || {}).length > 0 && (
                            <div className="text-[11px] text-amber-700" data-testid={`pt-adjustments-${row.state.replace(/\s+/g, "-")}`}>
                                Cycle-close: {Object.entries(row.period_adjustments).map(([m, v]) => `${MONTH_LABELS[m] || m} ${v >= 0 ? "+" : ""}${inr(v)}`).join(", ")}
                            </div>
                        )}
                        <div className="mt-2 flex gap-2">
                            {canEdit && (
                                <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`pt-edit-${row.state.replace(/\s+/g, "-")}`}>
                                    <PencilSimple size={12} className="mr-1" /> Edit
                                </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => loadHistory(row.state)} data-testid={`pt-history-${row.state.replace(/\s+/g, "-")}`}>
                                <Info size={12} className="mr-1" /> History
                            </Button>
                        </div>
                    </div>
                ))}
            </section>

            {/* Edit dialog ─────────────────────────────────────────── */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl" data-testid="pt-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>{draft._mode === "new" ? "Add State PT Rules" : `Edit PT Rules · ${draft.state}`}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[65vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>State</Label>
                                {draft._mode === "new" ? (
                                    <Input
                                        list="pt-state-suggestions"
                                        value={draft.state}
                                        onChange={(e) => setDraft({ ...draft, state: e.target.value })}
                                        placeholder="e.g. Maharashtra"
                                        data-testid="pt-edit-state-input"
                                    />
                                ) : (
                                    <div className="mt-1 text-sm font-medium text-slate-900">{draft.state}</div>
                                )}
                                <datalist id="pt-state-suggestions">
                                    {stateOptions.map((s) => <option key={s} value={s} />)}
                                </datalist>
                            </div>
                            <div>
                                <Label>Effective from *</Label>
                                <Input
                                    type="date"
                                    value={draft.effective_from}
                                    onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
                                    data-testid="pt-edit-effective-from"
                                />
                                <div className="text-[11px] text-slate-500 mt-1">Must be strictly future</div>
                            </div>
                        </div>

                        <div>
                            <Label>Cadence</Label>
                            <div className="mt-1 flex gap-4" data-testid="pt-edit-cadence">
                                {(meta.cadences || []).map((c) => (
                                    <label key={c} className="flex items-center gap-1.5 text-sm">
                                        <input
                                            type="radio"
                                            checked={draft.cadence === c}
                                            onChange={() => setDraft({ ...draft, cadence: c })}
                                            data-testid={`pt-cadence-${c}`}
                                        />
                                        {c === "monthly" ? "Monthly" : "Half-yearly"}
                                    </label>
                                ))}
                            </div>
                            {draft.cadence === "half_yearly" && (
                                <div className="mt-2">
                                    <div className="text-xs text-slate-600 mb-1">Half-yearly deduction months</div>
                                    <div className="flex flex-wrap gap-1.5" data-testid="pt-edit-hy-months">
                                        {(meta.months || []).map((m) => (
                                            <button
                                                key={m}
                                                type="button"
                                                onClick={() => toggleHalfYearlyMonth(m)}
                                                className={`text-[11px] px-2 py-1 rounded-full border ${draft.half_yearly_months.includes(m) ? "bg-indigo-100 border-indigo-400 text-indigo-900" : "bg-white border-slate-200 text-slate-500"}`}
                                                data-testid={`pt-hy-month-${m}`}
                                            >{MONTH_LABELS[m]}</button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <Label>Slabs</Label>
                                <Button size="sm" variant="outline" onClick={addSlab} data-testid="pt-add-slab-btn">
                                    <Plus size={12} className="mr-1" /> Add Slab
                                </Button>
                            </div>
                            <div className="mt-2 space-y-2" data-testid="pt-edit-slabs">
                                {draft.slabs.map((s, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                                        <Input
                                            type="number" step="1"
                                            value={s.min_gross}
                                            placeholder="Min gross"
                                            onChange={(e) => updateSlab(i, { min_gross: e.target.value })}
                                            data-testid={`pt-slab-min-${i}`}
                                        />
                                        <Input
                                            type="number" step="1"
                                            value={s.max_gross ?? ""}
                                            placeholder="Max gross (blank = ∞)"
                                            onChange={(e) => updateSlab(i, { max_gross: e.target.value === "" ? null : e.target.value })}
                                            data-testid={`pt-slab-max-${i}`}
                                        />
                                        <Input
                                            type="number" step="1"
                                            value={s.monthly_tax}
                                            placeholder="Monthly PT ₹"
                                            onChange={(e) => updateSlab(i, { monthly_tax: e.target.value })}
                                            data-testid={`pt-slab-tax-${i}`}
                                        />
                                        <Button size="sm" variant="ghost" onClick={() => removeSlab(i)} data-testid={`pt-slab-del-${i}`}>
                                            <Trash size={12} />
                                        </Button>
                                    </div>
                                ))}
                                {draft.slabs.length === 0 && (
                                    <div className="text-xs text-slate-400 italic">No slabs yet — click &quot;Add Slab&quot;.</div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={draft.gender_exemption_enabled}
                                    onChange={(e) => setDraft({ ...draft, gender_exemption_enabled: e.target.checked })}
                                    data-testid="pt-gender-ex-toggle"
                                />
                                <span>Gender exemption</span>
                            </label>
                            {draft.gender_exemption_enabled && (
                                <div className="mt-2 grid grid-cols-2 gap-2 pl-6">
                                    <div>
                                        <Label className="text-xs">Gender</Label>
                                        <select
                                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                            value={draft.gender_exemption.gender}
                                            onChange={(e) => setDraft({ ...draft, gender_exemption: { ...draft.gender_exemption, gender: e.target.value } })}
                                            data-testid="pt-gender-ex-gender"
                                        >
                                            {(meta.genders || []).map((g) => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Max gross (₹/mo)</Label>
                                        <Input
                                            type="number" step="1"
                                            value={draft.gender_exemption.max_gross}
                                            onChange={(e) => setDraft({ ...draft, gender_exemption: { ...draft.gender_exemption, max_gross: e.target.value } })}
                                            data-testid="pt-gender-ex-max"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <Label>Cycle-close month adjustments</Label>
                            <div className="text-[11px] text-slate-500 mb-1">Adds ₹X on top of the slab amount for the given month. Common: Maharashtra Feb +₹100 → ₹300 total.</div>
                            <div className="grid grid-cols-4 gap-2" data-testid="pt-period-adjustments">
                                {(meta.months || []).map((m) => (
                                    <div key={m} className="flex items-center gap-1">
                                        <span className="w-10 text-xs text-slate-600">{MONTH_LABELS[m]}</span>
                                        <Input
                                            type="number" step="1"
                                            value={draft.period_adjustments[m] ?? ""}
                                            onChange={(e) => setPeriodAdj(m, e.target.value)}
                                            placeholder="₹0"
                                            className="text-sm"
                                            data-testid={`pt-period-adj-${m}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label>Change note (optional)</Label>
                            <Textarea
                                value={draft.change_note}
                                onChange={(e) => setDraft({ ...draft, change_note: e.target.value })}
                                placeholder="Regulatory bulletin ref, memo id, etc."
                                rows={2}
                                data-testid="pt-change-note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditOpen(false)} data-testid="pt-edit-cancel">
                            <X size={14} className="mr-1" /> Cancel
                        </Button>
                        <Button onClick={submit} disabled={saving} data-testid="pt-edit-save">
                            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save as new version"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History dialog ─────────────────────────────────────── */}
            <Dialog open={!!historyState} onOpenChange={(v) => { if (!v) { setHistoryState(null); setHistoryRows([]); } }}>
                <DialogContent className="max-w-xl" data-testid="pt-history-dialog">
                    <DialogHeader>
                        <DialogTitle>History · {historyState}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {historyRows.length === 0 ? (
                            <div className="text-sm text-slate-400 py-6 text-center">No historical versions</div>
                        ) : historyRows.map((h) => (
                            <div key={h.doc_id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium text-slate-800">Effective {h.effective_from}{h.effective_to ? ` → ${h.effective_to}` : " → (current)"}</div>
                                    <Badge variant="outline" className="text-[10px]">{h.doc_id}</Badge>
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">by {h.created_by_email || "system"}</div>
                                <div className="text-[11px] text-slate-600 mt-1">
                                    {h.pt.slabs.length} slabs · {h.pt.cadence}
                                    {Object.keys(h.pt.period_adjustments || {}).length > 0 && <span> · Adj: {Object.entries(h.pt.period_adjustments).map(([m, v]) => `${MONTH_LABELS[m]} ${v >= 0 ? "+" : ""}${v}`).join(", ")}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setHistoryState(null); setHistoryRows([]); }}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
